/**
 * Composes the CT Brand/Courier Health matrix from the 6 freightsnapshot
 * "grouped_by_merchant" endpoints. Verified against the CT UI:
 *   - the 5 states PARTITION each column's total
 *   - column % = (total - breachedPending) / total
 *   - row % = stateCount / columnTotal
 *
 * Each snapshot endpoint returns groups: { _id:{merchant, courier, isBreached,
 *   firstAttemptWithinSLA, hasPickupFailed|hasDeliveryFailed|hasRtoDeliveryFailed},
 *   total, data:[orders] }.
 */
import _ from 'lodash'
import {ERROR, SUCCESS, WARNING, PRIMARY_BLUE} from '../../styles/foundation/colors'

// endpoint name → {kind (pipeline family), phase}
export const SNAPSHOT_SOURCES = {
  all_pickup_pending_orders_grouped_by_mechant: {kind: 'pickup', phase: 'pending', age: 'pickupPendingAgeingDays'},
  all_pickedup_orders_today_grouped_by_mechant: {kind: 'pickup', phase: 'done'},
  all_delivery_pending_orders_grouped_by_mechant: {kind: 'delivery', phase: 'pending', age: 'deliveryPendingAgeingDays'},
  all_delivered_orders_today_grouped_by_mechant: {kind: 'delivery', phase: 'done'},
  all_rto_delivery_pending_orders_grouped_by_mechant: {kind: 'rto', phase: 'pending', age: 'rtoDeliveryPendingAgeingDays'},
  all_rto_delivered_orders_today_grouped_by_mechant: {kind: 'rto', phase: 'done'},
}

// Column pipelines, in CT display order.
const PIPELINES = ['handover', 'delivery', 'pickup', 'rto']
const PIPELINE_LABEL = {handover: 'Handover Failed', delivery: 'Delivery', pickup: 'Pickup Pending', rto: 'RTO'}

const STATE_ROWS = [
  {key: 'breachedPending', label: 'Breached Pending', color: ERROR},
  {key: 'firstAttemptOnTime', label: 'First Attempt On Time', color: SUCCESS},
  {key: 'inQueue', label: 'In Queue', color: PRIMARY_BLUE},
  {key: 'completedAfterBreached', label: 'Completed After Breached', color: WARNING},
  {key: 'completedOnTime', label: 'Completed On Time', color: SUCCESS},
]

const AGE_BUCKETS = ['0-1 Days', '1-3 Days', '3-5 Days', '5-15 Days', '15+ Days']
const ageBucket = (d) => {
  if (d == null || isNaN(d)) return null
  if (d <= 1) return '0-1 Days'
  if (d <= 3) return '1-3 Days'
  if (d <= 5) return '3-5 Days'
  if (d <= 15) return '5-15 Days'
  return '15+ Days'
}

const pipelineOf = (kind, id) => (kind === 'pickup' ? (id.hasPickupFailed ? 'handover' : 'pickup') : kind)
// State classification — matches CT exactly (verified against live snapshot flag-combos):
//   • a breached shipment whose FIRST attempt was within SLA is "First Attempt On Time"
//     (the timely first attempt counts as good even though it later breached),
//   • a breached shipment without an on-time first attempt is "Breached Pending",
//   • anything not breached is "In Queue" (still within SLA / in flight).
const stateOf = (id, phase) => {
  if (phase === 'done') return id.isBreached ? 'completedAfterBreached' : 'completedOnTime'
  if (id.isBreached && id.firstAttemptWithinSLA === true) return 'firstAttemptOnTime'
  if (id.isBreached) return 'breachedPending'
  return 'inQueue'
}

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0)

// Resolve a snapshot `_id.courier` to a courier-account name via the couriers master.
function courierName(couriers, key) {
  if (!key) return 'Unknown'
  const c = couriers?.find((x) => x.id === key || x.cpAccountCode === key || x.name === key)
  return c?.name || c?.parent || key
}

// Resolve a snapshot `_id.courier` to its PARENT courier (for Courier Health rows).
function courierParent(couriers, key) {
  if (!key) return 'Unknown'
  const c = couriers?.find((x) => x.id === key || x.cpAccountCode === key || x.name === key)
  return c?.parent || c?.name || key
}

/**
 * Build one matrix entity from a flat list of tagged groups.
 * tagged = [{ id, total, data, kind, phase, age }]
 */
function buildEntity(name, tagged, couriers, {tier} = {}) {
  // accumulators[pipeline] = {total, states:{key:count}, ageing:{bucket:count},
  //   courierByState:{stateKey:{name:count}}}
  const acc = {}
  PIPELINES.forEach((p) => { acc[p] = {total: 0, states: {}, ageing: {}, courierByState: {}} })
  STATE_ROWS.forEach((s) => PIPELINES.forEach((p) => { acc[p].states[s.key] = 0; acc[p].courierByState[s.key] = {} }))

  for (const g of tagged) {
    const p = pipelineOf(g.kind, g.id)
    const st = stateOf(g.id, g.phase)
    const n = g.total || (g.data ? g.data.length : 0)
    acc[p].total += n
    acc[p].states[st] += n
    // per-courier breakdown for every state (drives the expandable child rows)
    const cn = courierName(couriers, g.id.courier)
    acc[p].courierByState[st][cn] = (acc[p].courierByState[st][cn] || 0) + n
    if (st === 'breachedPending') {
      // ageing buckets from order-level ageing days
      for (const o of (g.data || [])) {
        const b = ageBucket(o[g.age])
        if (b) acc[p].ageing[b] = (acc[p].ageing[b] || 0) + 1
      }
    }
  }

  const columns = PIPELINES.map((p) => ({
    label: PIPELINE_LABEL[p],
    score: pct(acc[p].total - acc[p].states.breachedPending, acc[p].total),
    criticalCount: acc[p].states.breachedPending,
    totalCount: acc[p].total,
  }))

  const cell = (p, count) => ({count, pct: pct(count, acc[p].total)})

  // Per-courier child rows for a state: union of couriers across pipelines, by total desc.
  const courierChildren = (stateKey) => {
    const totals = {}
    PIPELINES.forEach((p) => Object.entries(acc[p].courierByState[stateKey]).forEach(([cn, n]) => { totals[cn] = (totals[cn] || 0) + n }))
    return Object.keys(totals)
      .filter((cn) => totals[cn] > 0)
      .sort((a, b) => totals[b] - totals[a])
      .map((cn) => ({label: cn, cells: PIPELINES.map((p) => cell(p, acc[p].courierByState[stateKey][cn] || 0))}))
  }

  const rows = STATE_ROWS.map((s) => {
    const row = {key: s.key, label: s.label, color: s.color, cells: PIPELINES.map((p) => cell(p, acc[p].states[s.key]))}
    if (s.key === 'breachedPending') {
      // ageing buckets — only when there are breached-pending orders
      const bt = PIPELINES.reduce((sm, p) => sm + acc[p].states.breachedPending, 0)
      row.children = bt > 0 ? AGE_BUCKETS.map((b) => ({label: b, cells: PIPELINES.map((p) => cell(p, acc[p].ageing[b] || 0))})) : []
    } else {
      row.children = courierChildren(s.key)
    }
    return row
  })

  const totalAll = PIPELINES.reduce((sm, p) => sm + acc[p].total, 0)
  const breachedAll = PIPELINES.reduce((sm, p) => sm + acc[p].states.breachedPending, 0)
  // Open = still in the pipeline (non-completed): breached-pending + first-attempt + in-queue.
  const openAll = PIPELINES.reduce(
    (sm, p) => sm + acc[p].states.breachedPending + acc[p].states.firstAttemptOnTime + acc[p].states.inQueue,
    0,
  )
  return {
    name, tier,
    criticalCount: breachedAll,
    totalCount: totalAll,
    openCount: openAll,
    score: pct(totalAll - breachedAll, totalAll),
    columns, rows,
  }
}

// Flatten the {endpointName: groups[]} responses into tagged groups, filtered to a merchant.
function taggedGroups(responses, merchantId) {
  const out = []
  for (const [name, src] of Object.entries(SNAPSHOT_SOURCES)) {
    const groups = responses[name] || []
    for (const g of groups) {
      if (merchantId && g._id?.merchant !== merchantId) continue
      out.push({id: g._id || {}, total: g.total, data: g.data, kind: src.kind, phase: src.phase, age: src.age})
    }
  }
  return out
}

// Brand Health — one entity for the logged-in merchant.
export function buildBrandHealth(responses, merchant, couriers) {
  const tagged = taggedGroups(responses, merchant?.id)
  return buildEntity(merchant?.name || 'Your Brand', tagged, couriers, {tier: (merchant?.merchantTier || 'ENTERPRISE').toUpperCase()})
}

// Courier Health — one entity per PARENT courier (within the merchant's data);
// each state expands to that parent's accounts (via buildEntity's courier breakdown).
export function buildCourierHealth(responses, merchant, couriers) {
  const tagged = taggedGroups(responses, merchant?.id)
  const byParent = {}
  for (const g of tagged) {
    const p = courierParent(couriers, g.id.courier)
    if (!byParent[p]) byParent[p] = []
    byParent[p].push(g)
  }
  return Object.entries(byParent)
    .map(([p, groups]) => {
      const e = buildEntity(p, groups, couriers)
      // The exact courier-ACCOUNT ids this merchant actually ships with under this
      // parent (snapshot `_id.courier`). Drives the per-courier "View" so it filters
      // the Performance page by the right account(s) — a parent (e.g. XpressBees) can
      // span several accounts, so this is a list, not a single id.
      e.courierIds = _.uniq(groups.map((g) => g.id?.courier).filter(Boolean))
      return e
    })
    .filter((e) => e.totalCount > 0)
    .sort((a, b) => b.totalCount - a.totalCount)
}
